'use client';

import Link from '@/components/i18n/LocaleLink';
import Image from 'next/image';
import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight, Globe, ChevronDown, Menu } from 'lucide-react';
import type { Lang, SectionVisibility } from '../types';
import type { TranslationKeys } from '../translations';
import { langNames, langFlags } from '../lang-meta';

export interface DynamicNavItem {
  id: string;
  parentId: string | null;
  labelEs: string;
  labelEn: string;
  labelPt: string;
  kind: 'link' | 'landing' | 'dropdown';
  href: string | null;
  landingSlug: string | null;
  isActive: boolean;
  children?: DynamicNavItem[];
}

interface NavBarProps {
  t: TranslationKeys;
  lang: Lang;
  sections: SectionVisibility;
  langMenuOpen: boolean;
  setLangMenuOpen: (v: boolean) => void;
  onLangChange: (l: Lang) => void;
  setMobileMenuOpen: (v: boolean) => void;
  dynamicNav?: DynamicNavItem[];
  /** When true, hide the in-page scroll buttons (used on landing pages without sections) */
  hideSectionLinks?: boolean;
}

function pickLabel(item: DynamicNavItem, lang: Lang): string {
  if (lang === 'en') return item.labelEn || item.labelEs;
  if (lang === 'pt') return item.labelPt || item.labelEn || item.labelEs;
  if (lang === 'es') return item.labelEs;
  // de, fr, and any future languages: fall back to English
  return item.labelEn || item.labelEs;
}

function itemHref(item: DynamicNavItem): string {
  if (item.kind === 'landing' && item.landingSlug) return `/${item.landingSlug}`;
  return item.href || '#';
}

export default function NavBar({
  t,
  lang,
  sections,
  langMenuOpen,
  setLangMenuOpen,
  onLangChange,
  setMobileMenuOpen,
  dynamicNav,
  hideSectionLinks,
}: NavBarProps) {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const goToSection = (id: string) => {
    if (typeof window === 'undefined') return;

    // The landing lives under locale-prefixed paths ("/", "/es", "/en",
    // "/pt", "/de", "/fr"). The previous check only accepted "/" so visitors
    // on any localized root were redirected via window.location.href, which
    // triggered a full page load (losing the locale and breaking the
    // in-page anchor in Safari / Chrome — the user reports it as
    // "doesn't scroll" or "jumps weirdly when it arrives").
    const path = window.location.pathname.replace(/\/$/, '') || '/';
    const isLandingRoot = path === '/' || /^\/(en|es|pt|de|fr)$/.test(path);

    if (!isLandingRoot) {
      // Not on a landing root (e.g. /blog, /privacy, /demo). Go home in the
      // current locale and let the destination page resolve the hash.
      const match = path.match(/^\/(en|es|pt|de|fr)(\/|$)/);
      const localePrefix = match ? `/${match[1]}` : '';
      window.location.href = `${localePrefix}/#${id}`;
      return;
    }

    const el = document.getElementById(id);
    if (!el) return;

    // Header is fixed at the top with pt-6 + h-16 ≈ 88px. Use 96px to give
    // the destination a bit of breathing room so the anchor isn't tucked
    // right under the nav.
    const HEADER_OFFSET = 96;
    const top = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
    window.scrollTo({ top, behavior: 'smooth' });

    // Keep the URL in sync without triggering the browser's default
    // jump-to-anchor (which would override the smooth scroll above).
    if (window.history?.replaceState) {
      window.history.replaceState(null, '', `#${id}`);
    }
  };

  return (
      <nav className="fixed w-full z-50 pt-6 px-6 md:px-10 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="bg-purple-100 rounded-2xl px-6 overflow-visible">
            <div className="flex justify-between items-center h-16">
              {/* Logo */}
              <Link href="/" className="flex items-center">
                <Image
                  src="/images/brand/ezdocuai-logo.png"
                  alt="EZDocuAI"
                  width={36}
                  height={36}
                  className="h-9 w-9"
                  data-landing-brand-logo="true"
                  priority
                />
              </Link>

              {/* Mobile right side: compact auth buttons + burger */}
              <div className="flex md:hidden items-center gap-2 -mr-2">
                <Button asChild variant="ghost" size="sm" className="h-9 px-2.5 text-xs font-medium text-gray-700 hover:bg-purple-200 rounded-lg">
                  <Link href="/sign-in">
                    {t.login}
                  </Link>
                </Button>
                <Button asChild size="sm" className="h-9 px-3 text-xs font-semibold bg-purple-700 hover:bg-purple-800 text-white rounded-lg">
                  <Link href="/sign-up">
                    {t.getStarted}
                  </Link>
                </Button>
                <button
                  className="p-2 rounded-lg hover:bg-purple-200 transition-colors cursor-pointer touch-manipulation"
                  onClick={() => setMobileMenuOpen(true)}
                  aria-label={t.menuOpen}
                >
                  <Menu className="h-6 w-6 text-gray-700" />
                </button>
              </div>

              {/* Right side: Nav + Buttons (Desktop) */}
              <div className="hidden md:flex items-center gap-6" ref={dropdownRef}>
                {!hideSectionLinks && sections.showFeatures && (
                <button
                  onClick={() => goToSection('features')}
                  className="text-sm text-gray-700 hover:text-gray-900 transition-colors cursor-pointer"
                >
                  {t.features}
                </button>
                )}
                {!hideSectionLinks && (
                <button
                  onClick={() => goToSection('built-for')}
                  className="text-sm text-gray-700 hover:text-gray-900 transition-colors cursor-pointer"
                >
                  {t.builtForBadge}
                </button>
                )}
                {!hideSectionLinks && sections.showHowItWorks && (
                <button
                  onClick={() => goToSection('how-it-works')}
                  className="text-sm text-gray-700 hover:text-gray-900 transition-colors cursor-pointer"
                >
                  {t.howItWorks}
                </button>
                )}
                {!hideSectionLinks && sections.showPricing && (
                <button
                  onClick={() => goToSection('pricing')}
                  className="text-sm text-gray-700 hover:text-gray-900 transition-colors cursor-pointer"
                >
                  {t.pricing}
                </button>
                )}
                {!hideSectionLinks && sections.showFaq && (
                <button
                  onClick={() => goToSection('faq')}
                  className="text-sm text-gray-700 hover:text-gray-900 transition-colors cursor-pointer"
                >
                  {t.faq}
                </button>
                )}

                {/* Dynamic items managed in /admin/nav */}
                {dynamicNav?.map(item => {
                  if (item.kind === 'dropdown') {
                    const isOpen = openDropdown === item.id;
                    return (
                      <div key={item.id} className="relative">
                        <button
                          onClick={() => setOpenDropdown(isOpen ? null : item.id)}
                          className="flex items-center gap-1 text-sm text-gray-700 hover:text-gray-900 transition-colors cursor-pointer"
                        >
                          {pickLabel(item, lang)}
                          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {isOpen && item.children && item.children.length > 0 && (
                          <div className="absolute left-0 top-full mt-3 min-w-[220px] rounded-xl bg-white py-2 shadow-lg ring-1 ring-gray-200 z-[200]">
                            {item.children.map(child => (
                              <Link
                                key={child.id}
                                href={itemHref(child)}
                                onClick={() => setOpenDropdown(null)}
                                className="block px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700"
                              >
                                {pickLabel(child, lang)}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }
                  return (
                    <Link
                      key={item.id}
                      href={itemHref(item)}
                      className="text-sm text-gray-700 hover:text-gray-900 transition-colors"
                    >
                      {pickLabel(item, lang)}
                    </Link>
                  );
                })}

                <Button asChild variant="outline" size="sm" className="text-gray-700 border-gray-300 bg-white hover:bg-gray-50 rounded-lg ml-2">
                  <Link href="/sign-in">
                    {t.login}
                  </Link>
                </Button>
                <Button asChild size="sm" className="bg-purple-700 hover:bg-purple-800 text-white rounded-lg px-4">
                  <Link href="/sign-up">
                    {t.getStarted}
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Link>
                </Button>

                {/* Language Selector */}
                <div className="relative">
                  <button
                    onClick={() => setLangMenuOpen(!langMenuOpen)}
                    aria-label={`${t.languageLabel}: ${lang.toUpperCase()}`}
                    aria-haspopup="menu"
                    aria-expanded={langMenuOpen}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg hover:bg-purple-100 transition-colors cursor-pointer text-sm text-gray-700 border border-gray-200 hover:border-purple-300"
                  >
                    <Globe className="w-4 h-4 text-purple-500" />
                    <span className="font-medium">{lang.toUpperCase()}</span>
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${langMenuOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {langMenuOpen && (
                    <div className="absolute right-0 top-full mt-3 bg-white rounded-xl shadow-lg border border-gray-200 py-1 min-w-[120px] z-[200]">
                      {(['en', 'es', 'pt', 'de', 'fr'] as Lang[]).map((l) => (
                        <button
                          key={l}
                          onClick={() => onLangChange(l)}
                          className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2.5 hover:bg-purple-50 transition-colors cursor-pointer ${lang === l ? 'bg-purple-50 text-purple-600 font-semibold' : 'text-gray-700'}`}
                        >
                          <span>{langFlags[l]}</span>
                          <span>{langNames[l]}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>
      </nav>
  );
}
